import PropertyFormGeneric from "@rf/property/module/components/property-form/PropertyFormGeneric";

import {
  useGetDetailPropertyQuery,
  useUpdateRealEstateMutation
} from "@/src-refactor/modules/store/qrtk/services/realestate/realestate.qrtk";
import { UseFormReturn } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Grid, styled } from "@mui/material";
import MxEmptyCard from "@rf-l1/empty-sections/MxEmptyCard";
import MxEmptyIcon from "@rf-l1/empty-sections/MxEmptyIcon";
import MxEmptyBody from "@rf-l1/empty-sections/MxEmptyBody";
import MxLoading from "@rf-l1/loading/MxLoading";
import { C_PATH_ROUTES } from "@/src-refactor/modules/shared/model/const/path.const";
import patchPropertyProjectAdapter from "@rf/property/module/adapters/form/patch-project-property-adapter";
import UpdatePropertyProjectAdapter from "@rf/property/module/adapters/form/update-property-project-adapter";
import { useCallback, useEffect, useMemo } from "react";
import { useResetAllFormStates } from "@rf/property/module/components/property-form/store/propertyStore";
import dispatchMessagesOverlay from "@rf-l1/loading/mx-overlay-loading/util/dispatchMessagesOverlay";
import { PROPERTIES_MAIN_OVERLAY_ID } from "../../components/property-form/util/const/properties-overlays.const";
import toast from "react-hot-toast";
import { catchServerError } from "@/shared/helpers/catch-server-error";
import {
  CPropertyForm,
  CPropertyFormKeys,
  IPropertyForm
} from "../../components/property-form/util/schemas/property-schema/propertySchema";
import usePermissionProperty from "../../hooks/usePermissionProperty/usePermissionProperty";
import { QueryStatus } from "@reduxjs/toolkit/query";
import { HToastCatchServerError } from "@rf-helper/cath-error-server";

const PropertyProjectUpdatePage = () => {
  const [UPDATE_PROPERTY_M] = useUpdateRealEstateMutation();
  const { id } = useParams();
  const navigate = useNavigate();
  const resetAllFormStates = useResetAllFormStates();
  const { getPermissions } = usePermissionProperty();

  useEffect(() => {
    return () => {
      resetAllFormStates();
    };
  }, []);

  const { data, status } = useGetDetailPropertyQuery(
    { params: { id: id! } },
    { skip: !id }
  );

  useEffect(() => {
    console.log(status, "data", data);
    if (status == QueryStatus.fulfilled && data) {
      // Check if the user is an agent and if the property is not assigned to them
      const permissions = getPermissions({
        idStatusProperty: data.status_id,
        isProject: true,
        agents: data.agents.map(agent => ({
          id: agent.id,
          agencyId: agent.agency_id
        }))
      });

      if (!permissions.canUpdateProperty) {
        navigate(`${C_PATH_ROUTES.REALESTATE.DETAIL(id!)}`, { replace: true });
        toast.error("No tienes permisos para modificar esta propiedad");
      }

      window.dispatchEvent(
        new CustomEvent("load-property", {
          detail: data
        })
      );
    }
  }, [status, data]);

  const stateForm = useMemo(() => {
    if (!data) return null;
    if (!data.project) {
      navigate(`${C_PATH_ROUTES.REALESTATE.UPDATE(Number(id!))}`, {
        replace: true
      });
    }
    const detailData = patchPropertyProjectAdapter(data);
    return {
      ...detailData,
      [CPropertyFormKeys.is_project]: true,
      [CPropertyFormKeys.is_edit]: true
    } as unknown as IPropertyForm;
  }, [JSON.stringify(data)]);

  const handleSubmit = useCallback(
    async (v: IPropertyForm, helper: UseFormReturn<IPropertyForm>) => {
      try {
        dispatchMessagesOverlay(
          "Preparando actualización del proyecto...",
          PROPERTIES_MAIN_OVERLAY_ID
        );
        const body = UpdatePropertyProjectAdapter(v, stateForm as IPropertyForm);

        dispatchMessagesOverlay(
          "Actualizando proyecto...",
          PROPERTIES_MAIN_OVERLAY_ID
        );

        const res = (await UPDATE_PROPERTY_M({
          body,
          params: { id: id! }
        })
          .unwrap()
          .catch(e => {
            HToastCatchServerError(e);
          })) as any;

        if (res?.data?.id) {
          dispatchMessagesOverlay(
            "Proyecto actualizado con éxito",
            PROPERTIES_MAIN_OVERLAY_ID
          );
          helper.reset(CPropertyForm as unknown as IPropertyForm);
          navigate(`${C_PATH_ROUTES.REALESTATE.DETAIL(id!)}`, { replace: true });
        }
      } catch (e) {
        console.error(e);
      }
    },
    [UPDATE_PROPERTY_M, id, navigate, stateForm]
  );

  if (status == QueryStatus.pending || !stateForm) {
    return (
      <ContentLoading>
        <MxLoading variant="gradient" />
      </ContentLoading>
    );
  }

  if (status == QueryStatus.rejected || !id || !data) {
    return (
      <MxEmptyCard size="page">
        <MxEmptyIcon variant={status == QueryStatus.rejected ? "error" : "404"} />
        <MxEmptyBody>
          {status == QueryStatus.rejected
            ? "Ah ocurrido un inconveniente o la propiedad no fue encontrada"
            : "La propiedad no fue encontrada"}
        </MxEmptyBody>
      </MxEmptyCard>
    );
  }

  return <PropertyFormGeneric state={stateForm} onSubmit={handleSubmit} />;
};

export default PropertyProjectUpdatePage;

const ContentLoading = styled(Grid)`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  height: 70vh;
`;
